import { getPcMainScrollElement } from "./main-simplebar.js";

/** Figma 2007:2635 — つまみ最小高さ（デザイン上の 93px） */
const THUMB_MIN = 93;
const PC_MQ = "(min-width: 768px)";

let root = null;
let track = null;
let thumb = null;
let scrollEl = null;
let dragging = false;
let dragOffsetY = 0;
let rafId = 0;
let resizeObserver = null;
let mo = null;

function isPc() {
  return window.matchMedia(PC_MQ).matches;
}

function qs() {
  root = document.querySelector(".mainScroll");
  if (!root) return false;
  track = root.querySelector(".mainScroll__track");
  thumb = root.querySelector(".mainScroll__thumb");
  return Boolean(track && thumb);
}

/**
 * hidden のままだと track.clientHeight が 0 になるため、
 * 先に表示してからつまみを計測する。
 * PC では Figma どおり常時表示（オーバーフロー無しならつまみ＝軌道いっぱい）。
 */
function readScrollSizes(el) {
  if (!el) {
    return { scrollHeight: 0, clientHeight: 0, node: null };
  }
  /* SimpleBar 未取得時に .main が渡ってきた場合のフォールバック */
  if (el.classList && el.classList.contains("main")) {
    var wrapper = el.querySelector(".simplebar-content-wrapper");
    if (wrapper) el = wrapper;
  }
  return {
    scrollHeight: el.scrollHeight,
    clientHeight: el.clientHeight,
    node: el,
  };
}

function update() {
  rafId = 0;
  if (!root || !track || !thumb) return;
  if (!isPc()) {
    root.hidden = true;
    return;
  }

  var sizes = readScrollSizes(scrollEl);
  if (sizes.node && sizes.node !== scrollEl) {
    bindScrollEl(sizes.node);
    sizes = readScrollSizes(scrollEl);
  }

  var scrollHeight = sizes.scrollHeight;
  var clientHeight = sizes.clientHeight;
  var maxScroll = Math.max(0, scrollHeight - clientHeight);

  /* 計測のため先に表示（hidden の clientHeight=0 を避ける） */
  root.hidden = false;

  var trackHeight = track.clientHeight;
  if (trackHeight <= 0) return;

  var ratio = scrollHeight > 0 ? clientHeight / scrollHeight : 1;
  if (ratio > 1) ratio = 1;
  var thumbHeight =
    maxScroll <= 1
      ? trackHeight
      : Math.min(
          trackHeight,
          Math.max(THUMB_MIN, Math.round(trackHeight * ratio))
        );
  var travel = Math.max(0, trackHeight - thumbHeight);
  var progress =
    maxScroll > 0 && scrollEl ? scrollEl.scrollTop / maxScroll : 0;

  thumb.style.height = thumbHeight + "px";
  thumb.style.transform = "translateY(" + travel * progress + "px)";
  root.setAttribute("aria-valuenow", String(Math.round(progress * 100)));
}

function requestUpdate() {
  if (rafId) return;
  rafId = requestAnimationFrame(update);
}

function getTravelMetrics() {
  if (!scrollEl || !track || root.hidden) return null;
  const scrollHeight = scrollEl.scrollHeight;
  const clientHeight = scrollEl.clientHeight;
  const maxScroll = Math.max(0, scrollHeight - clientHeight);
  const trackHeight = track.clientHeight;
  if (trackHeight <= 0 || maxScroll <= 0) return null;
  const ratio = clientHeight / scrollHeight;
  const thumbHeight = Math.min(
    trackHeight,
    Math.max(THUMB_MIN, Math.round(trackHeight * ratio))
  );
  const travel = Math.max(0, trackHeight - thumbHeight);
  return { maxScroll: maxScroll, thumbHeight: thumbHeight, travel: travel };
}

function setScrollFromThumbTop(thumbTop) {
  const metrics = getTravelMetrics();
  if (!metrics || !scrollEl || metrics.travel <= 0) return;
  const clamped = Math.min(metrics.travel, Math.max(0, thumbTop));
  scrollEl.scrollTop = (clamped / metrics.travel) * metrics.maxScroll;
}

function onScroll() {
  if (dragging) return;
  requestUpdate();
}

function onPointerDownThumb(e) {
  if (!isPc() || !thumb || root.hidden) return;
  e.preventDefault();
  e.stopPropagation();
  dragging = true;
  root.classList.add("is-dragging");
  const rect = thumb.getBoundingClientRect();
  dragOffsetY = e.clientY - rect.top;
  if (typeof thumb.setPointerCapture === "function") {
    thumb.setPointerCapture(e.pointerId);
  }
}

function onPointerMove(e) {
  if (!dragging || !track) return;
  const trackRect = track.getBoundingClientRect();
  const thumbTop = e.clientY - trackRect.top - dragOffsetY;
  setScrollFromThumbTop(thumbTop);
  update();
}

function onPointerUp(e) {
  if (!dragging) return;
  dragging = false;
  if (root) root.classList.remove("is-dragging");
  if (thumb && typeof thumb.releasePointerCapture === "function") {
    try {
      thumb.releasePointerCapture(e.pointerId);
    } catch (err) {
      /* ignore */
    }
  }
  requestUpdate();
}

function onTrackPointerDown(e) {
  if (!isPc() || !track || !thumb || root.hidden) return;
  if (e.target === thumb || thumb.contains(e.target)) return;
  e.preventDefault();
  const metrics = getTravelMetrics();
  if (!metrics) return;
  const trackRect = track.getBoundingClientRect();
  const thumbTop = e.clientY - trackRect.top - metrics.thumbHeight / 2;
  setScrollFromThumbTop(thumbTop);
  update();
}

function bindScrollEl(el) {
  if (scrollEl === el) {
    requestUpdate();
    return;
  }
  if (scrollEl) {
    scrollEl.removeEventListener("scroll", onScroll);
  }
  scrollEl = el;
  if (scrollEl) {
    scrollEl.addEventListener("scroll", onScroll, { passive: true });
  }
  requestUpdate();
}

function observeMain() {
  const main = document.querySelector(".main");
  if (!main) return;

  if (resizeObserver) resizeObserver.disconnect();
  resizeObserver = new ResizeObserver(function () {
    requestUpdate();
  });
  resizeObserver.observe(main);
  if (scrollEl) resizeObserver.observe(scrollEl);

  if (mo) mo.disconnect();
  mo = new MutationObserver(function () {
    requestUpdate();
  });
  mo.observe(main, { childList: true, subtree: true, characterData: true });
}

/**
 * SimpleBar 同期後などに呼ぶ。スクロール要素を取り直して UI を更新。
 */
export function syncMainScrollbar() {
  if (!qs()) return;
  const main = document.querySelector(".main");
  if (main && !main.id) {
    main.id = "main";
  }
  if (!isPc()) {
    root.hidden = true;
    return;
  }
  bindScrollEl(getPcMainScrollElement());
  observeMain();
  requestUpdate();
  /* 画像・フォント読み込み後に再計測 */
  window.setTimeout(requestUpdate, 100);
  window.setTimeout(requestUpdate, 500);
}

export function initMainScrollbar() {
  if (!qs()) return;

  track.addEventListener("pointerdown", onTrackPointerDown);
  thumb.addEventListener("pointerdown", onPointerDownThumb);
  window.addEventListener("pointermove", onPointerMove);
  window.addEventListener("pointerup", onPointerUp);
  window.addEventListener("pointercancel", onPointerUp);
  window.addEventListener("resize", requestUpdate);
  window.addEventListener("load", requestUpdate);

  syncMainScrollbar();
  window.matchMedia(PC_MQ).addEventListener("change", syncMainScrollbar);
}
